import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Locale } from '../../../api/types';
import { Button } from '../../../shared/ui/Button';
import { Card } from '../../../shared/ui/Card';
import { PageHeader } from '../../../shared/ui/PageHeader';
import { Spinner } from '../../../shared/ui/Spinner';
import { useAppSettings } from '../hooks/useAppSettings';
import { useUpdateAppSettings } from '../hooks/useUpdateAppSettings';
import { LocalesTab } from '../components/LocalesTab';
import { NotificationsTab } from '../components/NotificationsTab';
import { ReferenceDataTab } from '../components/ReferenceDataTab';
import styles from './SettingsPage.module.css';

type Tab = 'locales' | 'notifications' | 'reference';

const TABS: Tab[] = ['locales', 'notifications', 'reference'];

// PARA-106, AC-4/AC-5/AC-6/AC-10: справочники + редактирование.
export function SettingsPage(): JSX.Element {
  const { t } = useTranslation();
  const { data } = useAppSettings();
  const { mutate, isPending } = useUpdateAppSettings();
  const [tab, setTab] = useState<Tab>('locales');

  const [locales, setLocales] = useState<Locale[]>([]);
  const [quietHoursStart, setQuietHoursStart] = useState('22:00');
  const [quietHoursEnd, setQuietHoursEnd] = useState('08:00');
  const [maxPerDay, setMaxPerDay] = useState(2);

  useEffect(() => {
    if (!data) {
      return;
    }
    setLocales(data.supported_locales);
    setQuietHoursStart(data.quiet_hours_start.slice(0, 5));
    setQuietHoursEnd(data.quiet_hours_end.slice(0, 5));
    setMaxPerDay(data.max_notifications_per_day);
  }, [data]);

  if (!data) {
    return <Spinner />;
  }

  const handleSave = (): void => {
    mutate({
      supported_locales: locales,
      quiet_hours_start: `${quietHoursStart}:00`,
      quiet_hours_end: `${quietHoursEnd}:00`,
      max_notifications_per_day: maxPerDay,
    });
  };

  return (
    <section>
      <PageHeader title={t('settings.title')} />
      <div className={styles.stack}>
        <nav className={styles.tabs}>
          {TABS.map((item) => (
            <button
              key={item}
              type="button"
              aria-current={tab === item}
              className={[styles.tab, tab === item ? styles.tabActive : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => setTab(item)}
            >
              {t(`settings.tabs.${item}`)}
            </button>
          ))}
        </nav>

        <Card>
          {tab === 'locales' && <LocalesTab activeLocales={locales} onChange={setLocales} />}
          {tab === 'notifications' && (
            <NotificationsTab
              quietHoursStart={quietHoursStart}
              quietHoursEnd={quietHoursEnd}
              maxNotificationsPerDay={maxPerDay}
              onChange={(patch) => {
                if (patch.quietHoursStart !== undefined) setQuietHoursStart(patch.quietHoursStart);
                if (patch.quietHoursEnd !== undefined) setQuietHoursEnd(patch.quietHoursEnd);
                if (patch.maxNotificationsPerDay !== undefined) {
                  setMaxPerDay(patch.maxNotificationsPerDay);
                }
              }}
            />
          )}
          {tab === 'reference' && (
            <ReferenceDataTab taskSizes={data.task_sizes} taskStatuses={data.task_statuses} />
          )}
        </Card>

        {tab !== 'reference' && (
          <div className={styles.footer}>
            <Button type="button" disabled={isPending} onClick={handleSave}>
              {t('settings.save')}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
